import { useCallback, useEffect, useRef, useState, type KeyboardEvent } from 'react';
import type { SolutionMapEntry } from '../../../client/IvtHubClient';
import {
  PRIORITY_BG,
  PRIORITY_BG_EDIT,
  priorityTier,
} from '../../../utils/priority';

function CellName({
  useCase,
  locked,
  onSingleClick,
  onDoubleClick,
}: {
  useCase: string;
  locked: boolean;
  onSingleClick: () => void;
  onDoubleClick: () => void;
}) {
  const clickTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleClick = useCallback(() => {
    if (locked) return;
    if (clickTimer.current) {
      clearTimeout(clickTimer.current);
      clickTimer.current = null;
      onDoubleClick();
    } else {
      clickTimer.current = setTimeout(() => {
        clickTimer.current = null;
        onSingleClick();
      }, 250);
    }
  }, [locked, onSingleClick, onDoubleClick]);

  return (
    <span
      className="ivt-sm-cell__uc-name ivt-sm-cell__uc-name--editable"
      onClick={handleClick}
      title="Click to cycle priority; double-click to rename; drag to reorder"
    >
      {useCase}
    </span>
  );
}


export interface SolutionMapCellProps {
  entry: SolutionMapEntry;
  editMode?: boolean;
  locked?: boolean;
  isFirst?: boolean;
  isLast?: boolean;
  highlighted?: boolean;
  dimmed?: boolean;
  onCyclePriority?: (entry: SolutionMapEntry) => void;
  onMove?: (entry: SolutionMapEntry, direction: 'UP' | 'DOWN') => void;
  onRename?: (entry: SolutionMapEntry, newName: string) => void;
  onSoftDelete?: (entry: SolutionMapEntry) => void;
}

/**
 * One UC cell in an industry matrix column. Two modes:
 *  - read-only → priority-tinted background, UC name only (no asset chips —
 *                Solution Map is a strategic Area×UC view, asset linkage
 *                lives elsewhere)
 *  - editMode  → S/R/O chip + double-click rename textarea + ▲▼ + ✕
 */
export default function SolutionMapCell({
  entry,
  editMode = false,
  locked = false,
  isFirst = false,
  isLast = false,
  highlighted = false,
  dimmed = false,
  onCyclePriority,
  onMove,
  onRename,
  onSoftDelete,
}: SolutionMapCellProps) {
  if (editMode) {
    return (
      <EditableCell
        entry={entry}
        locked={locked}
        isFirst={isFirst}
        isLast={isLast}
        onCyclePriority={onCyclePriority}
        onMove={onMove}
        onRename={onRename}
        onSoftDelete={onSoftDelete}
      />
    );
  }

  const tier = priorityTier(entry.priority);
  const cls =
    'ivt-sm-cell' +
    (highlighted ? ' ivt-sm-cell--highlighted' : '') +
    (dimmed ? ' ivt-sm-cell--dimmed' : '');
  return (
    <div
      className={cls}
      style={{ background: PRIORITY_BG[tier] }}
    >
      <span className="ivt-sm-cell__uc-name">{entry.useCase}</span>
    </div>
  );
}

interface EditableCellProps {
  entry: SolutionMapEntry;
  locked: boolean;
  isFirst: boolean;
  isLast: boolean;
  onCyclePriority?: SolutionMapCellProps['onCyclePriority'];
  onMove?: SolutionMapCellProps['onMove'];
  onRename?: SolutionMapCellProps['onRename'];
  onSoftDelete?: SolutionMapCellProps['onSoftDelete'];
}

function EditableCell({
  entry,
  locked,
  isFirst,
  isLast,
  onCyclePriority,
  onMove,
  onRename,
  onSoftDelete,
}: EditableCellProps) {
  const [renaming, setRenaming] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const cellRef = useRef<HTMLDivElement | null>(null);
  const tier = priorityTier(entry.priority);
  const useCase = entry.useCase ?? '';

  useEffect(() => {
    if (renaming && textareaRef.current) {
      textareaRef.current.focus();
      textareaRef.current.select();
    }
  }, [renaming]);

  const commitRename = () => {
    if (!textareaRef.current) return;
    const raw = textareaRef.current.value.trim().replace(/\n/g, ' ');
    if (raw && raw !== useCase) {
      onRename?.(entry, raw);
    }
    setRenaming(false);
  };

  const cancelRename = () => setRenaming(false);

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      cancelRename();
    } else if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      commitRename();
    }
  };

  const [dragging, setDragging] = useState(false);
  const [offsetY, setOffsetY] = useState(0);
  const offsetRef = useRef(0);

  const canMoveUp = !isFirst;
  const canMoveDown = !isLast;

  const handleMouseDown = (e: React.MouseEvent) => {
    if (locked || renaming) return;
    if ((e.target as HTMLElement).closest('.ivt-sm-cell__del-btn')) return;
    if ((e.target as HTMLElement).closest('.ivt-sm-cell__rename-input')) return;
    const startY = e.clientY;
    let started = false;

    const cellHeight = cellRef.current?.getBoundingClientRect().height ?? 57;
    const maxUp = canMoveUp ? -cellHeight : 0;
    const maxDown = canMoveDown ? cellHeight : 0;
    const DEAD_ZONE = 5;

    const onMouseMove = (ev: MouseEvent) => {
      const dy = ev.clientY - startY;
      if (!started && Math.abs(dy) < DEAD_ZONE) return;
      if (!started) {
        started = true;
        setDragging(true);
      }
      const clamped = Math.max(maxUp, Math.min(maxDown, dy));
      offsetRef.current = clamped;
      setOffsetY(clamped);
    };

    const onMouseUp = () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      if (!started) return;
      setDragging(false);
      setOffsetY(0);

      const currentOffset = offsetRef.current;
      const threshold = cellHeight * 0.4;
      if (currentOffset < -threshold && canMoveUp) {
        onMove?.(entry, 'UP');
      } else if (currentOffset > threshold && canMoveDown) {
        onMove?.(entry, 'DOWN');
      }
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  };

  return (
    <div
      ref={cellRef}
      className={'ivt-sm-cell ivt-sm-cell--editing' + (dragging ? ' ivt-sm-cell--dragging' : '')}
      style={{
        background: PRIORITY_BG_EDIT[tier],
        transform: dragging ? `translateY(${offsetY}px)` : undefined,
        zIndex: dragging ? 10 : undefined,
      }}
      onMouseDown={handleMouseDown}
    >
      {renaming ? (
        <textarea
          ref={textareaRef}
          className="ivt-sm-cell__rename-input"
          defaultValue={useCase}
          onBlur={commitRename}
          onKeyDown={handleKeyDown}
          disabled={locked}
        />
      ) : (
        <CellName
          useCase={useCase}
          locked={locked}
          onSingleClick={() => onCyclePriority?.(entry)}
          onDoubleClick={() => setRenaming(true)}
        />
      )}

      <div className="ivt-sm-cell__right-col">
        <button
          type="button"
          className="ivt-sm-cell__del-btn"
          disabled={locked}
          onClick={(e) => { e.stopPropagation(); onSoftDelete?.(entry); }}
          title="Delete"
        >
          ✕
        </button>
        <span className="ivt-sm-cell__drag-handle" title="Drag to reorder">
          <svg width="10" height="26" viewBox="0 0 10 26" fill="none">
            <circle cx="5" cy="4" r="3" fill="#d97706" stroke="#92400e" strokeWidth="1"/>
            <circle cx="5" cy="13" r="3" fill="#2563eb" stroke="#1d4ed8" strokeWidth="1"/>
            <circle cx="5" cy="22" r="3" fill="#9ca3af" stroke="#6b7280" strokeWidth="1"/>
          </svg>
        </span>
      </div>
    </div>
  );
}
